import { useEffect, useMemo, useState } from 'react';
import { Package, ShieldCheck, Warehouse, Waves } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import {
  useAccount,
  useReadContract,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { ERC20_ABI, ROUTER_ABI, SALT_RWA_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import SaltPriceChart from './rwa/SaltPriceChart';
import WarehouseComposition from './rwa/WarehouseComposition';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

const DEADLINE_SECONDS = 20 * 60;
const SLIPPAGE_PERCENT = 0.5;

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

function fmtKg(value: bigint | undefined) {
  if (value === undefined) return '-';
  return `${Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: 0 })} kg`;
}

export default function RwaView() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const rwa = deployment?.saltRwa;

  const { data: totalSupply } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'totalSupply',
    chainId: readChainId,
    query: { enabled: Boolean(rwa), refetchInterval: 10000 },
  });

  const { data: totalRedeemed } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'totalRedeemed',
    chainId: readChainId,
    query: { enabled: Boolean(rwa), refetchInterval: 10000 },
  });

  const { data: referencePrice } = useReadContract({
    address: rwa?.token,
    abi: SALT_RWA_ABI,
    functionName: 'referencePriceUsd',
    chainId: readChainId,
    query: { enabled: Boolean(rwa) },
  });

  // live AMM quote for 1 SALT → USDT, so users see the tradable price next to
  // the issuer's attested reference price
  const { data: poolQuote } = useReadContract({
    address: deployment?.router,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: rwa && deployment ? [parseUnits('1', 18), [rwa.token, deployment.tokens.USDT]] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(rwa && deployment), refetchInterval: 10000 },
  });
  const poolPrice = poolQuote ? Number(formatUnits(poolQuote[1], 18)) : undefined;

  if (!rwa) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Salt RWA is not deployed on this network.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Waves size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Salt RWA</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            실물 소금 창고 보관증 토큰 — 1 SALT = 창고에 보관된 소금 1 kg
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Package} label="토큰화된 재고" value={fmtKg(totalSupply)} />
        <StatCard icon={ShieldCheck} label="실물 상환 완료" value={fmtKg(totalRedeemed)} />
        <StatCard
          icon={Warehouse}
          label="발행사 기준가"
          value={referencePrice !== undefined ? `$${(Number(referencePrice) / 1e8).toFixed(2)}/kg` : '-'}
        />
        <StatCard
          icon={Waves}
          label="실시간 풀 가격"
          value={poolPrice !== undefined ? `${poolPrice.toFixed(4)} USDT/kg` : '-'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0">
          <SaltPriceChart
            poolPrice={poolPrice}
            referenceUsd={referencePrice !== undefined ? Number(referencePrice) / 1e8 : undefined}
          />
        </div>
        <div className="min-w-0">
          <WarehouseComposition
            tokenAddress={rwa.token}
            chainId={readChainId}
            totalSupply={totalSupply}
            totalRedeemed={totalRedeemed}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <LotRegistry tokenAddress={rwa.token} chainId={readChainId} />
        </div>
        <div className="lg:col-span-2 space-y-6">
          <TradePanel />
          <RedeemPanel tokenAddress={rwa.token} chainId={readChainId} />
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        BSC 테스트넷 프로토타입입니다. 로트 정보는 온체인에 기록된 실제 데이터이며, 상환 요청 시 토큰이
        실제로 소각되지만 물류 배송은 데모 단계에서는 이루어지지 않습니다. 발행·소각 권한은 발행사
        지갑으로 제한되어 있습니다.
      </div>
      <TrustBar contract={rwa.token} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Package; label: string; value: string }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-xs text-foam-500">
        <Icon size={13} />
        {label}
      </div>
      <div className="text-xl font-semibold text-foam-100 mt-1.5 tabular-nums">{value}</div>
    </div>
  );
}

function LotRegistry({ tokenAddress, chainId }: { tokenAddress: `0x${string}`; chainId: number }) {
  const { data: lotCount } = useReadContract({
    address: tokenAddress,
    abi: SALT_RWA_ABI,
    functionName: 'lotCount',
    chainId,
  });

  const lotContracts = useMemo(() => {
    if (lotCount === undefined) return [];
    return Array.from({ length: Number(lotCount) }, (_, i) => ({
      address: tokenAddress,
      abi: SALT_RWA_ABI,
      functionName: 'lots',
      args: [BigInt(i)],
      chainId,
    }) as const);
  }, [lotCount, tokenAddress, chainId]);

  const { data: lots } = useReadContracts({ contracts: lotContracts, query: { enabled: lotContracts.length > 0 } });

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
      <h3 className="text-sm font-semibold text-foam-100 mb-3">창고 로트 원장 (온체인)</h3>
      {!lots ? (
        <div className="text-xs text-foam-500 py-4 text-center">로트 정보를 불러오는 중...</div>
      ) : (
        <div className="space-y-3">
          {lots.map((lot, i) => {
            if (lot.status !== 'success') return null;
            const [batchId, kg, warehouse, producedAt, certification] = lot.result as readonly [
              string,
              bigint,
              string,
              bigint,
              string,
            ];
            return (
              <div key={batchId} className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/50">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold text-foam-100">{batchId}</span>
                  <span className="text-xs font-semibold text-tide-400 tabular-nums">
                    {Number(kg).toLocaleString('en-US')} kg
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-foam-500">
                  <span className="flex items-center gap-1">
                    <Warehouse size={11} />
                    {warehouse}
                  </span>
                  <span>생산일: {new Date(Number(producedAt) * 1000).toLocaleDateString('ko-KR')}</span>
                  <span className="col-span-2 flex items-center gap-1 text-tide-400/80">
                    <ShieldCheck size={11} />
                    {certification}
                  </span>
                </div>
                <div className="text-[10px] text-foam-700 mt-1.5">Lot #{i}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TradePanel() {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const rwa = deployment?.saltRwa;

  const [mode, setMode] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const usdtAddress = deployment?.tokens.USDT;
  const fromAddress = mode === 'buy' ? usdtAddress : rwa?.token;
  const path = useMemo(() => {
    if (!rwa || !usdtAddress) return null;
    return mode === 'buy' ? [usdtAddress, rwa.token] : [rwa.token, usdtAddress];
  }, [mode, rwa, usdtAddress]);

  const amountWei = safeParse(amount);

  const { data: fromBalance, refetch: refetchBalance } = useReadContract({
    address: fromAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(fromAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: fromAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment ? [address, deployment.router] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(fromAddress && address && deployment) },
  });

  const { data: amountsOut } = useReadContract({
    address: deployment?.router,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: amountWei && path ? [amountWei, path] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(amountWei && path && deployment) },
  });
  const outWei = amountsOut ? amountsOut[amountsOut.length - 1] : undefined;

  const needsApproval = amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract: writeSwap, data: swapHash, isPending: isSwapPending, reset: resetSwap } = useWriteContract();
  const { isLoading: isSwapConfirming, isSuccess: isSwapConfirmed, error: swapReceiptError, refetch: retrySwapReceipt } = useWaitForTransactionReceipt({ hash: swapHash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isSwapConfirmed && swapHash && address) {
      logTransaction({
        type: mode === 'buy' ? 'rwaBuy' : 'rwaSell',
        hash: swapHash,
        account: address,
        chainId: readChainId,
        details: { pool: 'SALT-USDT', amount },
      });
      setAmount('');
      refetchBalance();
      resetSwap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwapConfirmed]);

  const handleApprove = () => {
    if (!fromAddress || !deployment) return;
    setTxError(null);
    writeApprove(
      { address: fromAddress, abi: ERC20_ABI, functionName: 'approve', args: [deployment.router, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleSwap = () => {
    if (!amountWei || !path || !deployment || !address || !outWei) return;
    setTxError(null);
    const amountOutMin = (outWei * BigInt(Math.round((100 - SLIPPAGE_PERCENT) * 100))) / 10000n;
    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_SECONDS);
    writeSwap(
      {
        address: deployment.router,
        abi: ROUTER_ABI,
        functionName: 'swapExactTokensForTokens',
        args: [amountWei, amountOutMin, path, address, deadline],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const isPending = isApprovePending || isApproveConfirming || isSwapPending || isSwapConfirming;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (isApprovePending || isApproveConfirming) {
      buttonLabel = 'Approving...';
    } else if (needsApproval) {
      buttonLabel = `Approve ${mode === 'buy' ? 'USDT' : 'SALT'}`;
      buttonAction = handleApprove;
      buttonDisabled = false;
    } else if (isSwapPending || isSwapConfirming) {
      buttonLabel = '처리 중...';
    } else {
      buttonLabel = mode === 'buy' ? 'SALT 매수' : 'SALT 매도';
      buttonAction = handleSwap;
      buttonDisabled = !outWei;
    }
  }

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foam-100">SALT 거래</h3>
        <div className="flex gap-1 p-0.5 rounded-lg bg-abyss-900 border border-abyss-700">
          {(['buy', 'sell'] as const).map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setAmount('');
                setTxError(null);
              }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === m
                  ? m === 'buy'
                    ? 'bg-tide-500 text-abyss-950'
                    : 'bg-coral-500 text-abyss-950'
                  : 'text-foam-300 hover:text-foam-100'
              }`}
            >
              {m === 'buy' ? '매수' : '매도'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60 mb-2">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>{mode === 'buy' ? 'USDT 지불' : 'SALT 판매 (kg)'}</span>
          <span>
            잔액: {fromBalance !== undefined ? Number(formatUnits(fromBalance, 18)).toFixed(4) : '-'}
          </span>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      {amountWei && outWei !== undefined && (
        <div className="text-xs text-foam-500 mb-2 flex justify-between">
          <span>예상 수령</span>
          <span className="text-foam-300 tabular-nums">
            {Number(formatUnits(outWei, 18)).toFixed(4)} {mode === 'buy' ? 'SALT (kg)' : 'USDT'}
          </span>
        </div>
      )}

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || isPending}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {buttonLabel}
      </button>

      {txError && (
        <div className="mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: swapReceiptError, hash: swapHash, retry: retrySwapReceipt },
      ]} />
      {isSwapConfirmed && !txError && (
        <div className="mt-2 px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">거래가 체결되었습니다.</div>
      )}
    </div>
  );
}

function RedeemPanel({ tokenAddress, chainId }: { tokenAddress: `0x${string}`; chainId: number }) {
  const { address, isConnected } = useAccount();
  const onCorrectChain = chainId === TARGET_CHAIN.id;

  const [kg, setKg] = useState('');
  const [deliveryInfo, setDeliveryInfo] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const amountWei = safeParse(kg);

  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: { enabled: Boolean(address) },
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });

  useEffect(() => {
    if (isConfirmed && hash && address) {
      logTransaction({
        type: 'rwaRedeem',
        hash,
        account: address,
        chainId,
        details: { pool: 'SALT-USDT', kg },
      });
      setKg('');
      setDeliveryInfo('');
      refetchBalance();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  const handleRedeem = () => {
    if (!amountWei) return;
    setTxError(null);
    writeContract(
      {
        address: tokenAddress,
        abi: SALT_RWA_ABI,
        functionName: 'redeem',
        args: [amountWei, deliveryInfo],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const insufficient = amountWei !== null && balance !== undefined && balance < amountWei;
  const belowMin = amountWei !== null && amountWei < parseUnits('1', 18);
  const disabled =
    !isConnected || !onCorrectChain || !amountWei || insufficient || belowMin || !deliveryInfo.trim() || isPending || isConfirming;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
      <h3 className="text-sm font-semibold text-foam-100 mb-1">실물 상환</h3>
      <p className="text-[11px] text-foam-500 mb-3">
        SALT를 소각하고 창고의 실물 소금 배송을 요청합니다 (최소 1 kg).
      </p>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60 mb-2">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>상환 수량 (kg)</span>
          <span>보유: {balance !== undefined ? Number(formatUnits(balance, 18)).toFixed(4) : '-'} SALT</span>
        </div>
        <input
          value={kg}
          onChange={(e) => setKg(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0"
          className="w-full bg-transparent text-xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <input
        value={deliveryInfo}
        onChange={(e) => setDeliveryInfo(e.target.value)}
        placeholder="배송지 / 연락처 (예: 서울시 ○○구 ..., 010-...)"
        className="w-full px-3 py-2.5 rounded-lg border border-abyss-700 bg-abyss-900/60 text-xs text-foam-100 outline-none placeholder:text-foam-700 mb-2"
      />

      <button
        onClick={handleRedeem}
        disabled={disabled}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {isPending || isConfirming
          ? '처리 중...'
          : insufficient
            ? 'SALT 잔액 부족'
            : belowMin
              ? '최소 1 kg'
              : '상환 요청 (토큰 소각)'}
      </button>

      {txError && (
        <div className="mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[{ error: receiptError, hash, retry: retryReceipt }]} />
      {isConfirmed && !txError && (
        <div className="mt-2 px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
          상환 요청이 온체인에 기록되었습니다. 토큰이 소각되었습니다.
        </div>
      )}
    </div>
  );
}
